import { cn } from '@cobliteam/ui/utils';
import type { Transition, Variants } from 'motion/react';
import { motion, useAnimation } from 'motion/react';
import type { CSSProperties, HTMLAttributes } from 'react';
import { forwardRef, useEffect } from 'react';

export interface PaletteIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface PaletteIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  isHovered: boolean;
}

const defaultTransition: Transition = {
  duration: 0.6,
  opacity: { duration: 0.2 },
};

const paletteVariants: Variants = {
  normal: {
    pathLength: 1,
    opacity: 1,
  },
  animate: {
    pathLength: [0, 1],
    opacity: [0, 1],
  },
};

// Each color dot pops in once the palette outline is drawn, staggered around the palette.
const makeDotVariants = (delay: number): Variants => ({
  normal: {
    scale: 1,
    opacity: 1,
  },
  animate: {
    scale: [0, 1],
    opacity: [0, 1],
    transition: { delay, duration: 0.2, ease: 'easeOut' },
  },
});

const dotVariants: Variants[] = [makeDotVariants(0.45), makeDotVariants(0.55), makeDotVariants(0.65), makeDotVariants(0.75)];

// transform-box keeps the scale animation centered on each dot instead of the SVG origin.
const dotStyle: CSSProperties = { transformBox: 'fill-box', transformOrigin: 'center' };

const PaletteIcon = forwardRef<PaletteIconHandle, PaletteIconProps>(({ onMouseEnter, onMouseLeave, className, size = 28, isHovered, ...props }, _ref) => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start(isHovered ? 'animate' : 'normal');
  }, [isHovered, controls]);

  return (
    <div className={cn(className)} {...props}>
      <svg
        data-testid='lucide-palette'
        xmlns='http://www.w3.org/2000/svg'
        width={18}
        height={18}
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <motion.path
          d='M12 22a1 1 0 0 1 0-20 10 9 0 0 1 10 9 5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8z'
          variants={paletteVariants}
          transition={defaultTransition}
          animate={controls}
        />
        <motion.circle cx='13.5' cy='6.5' r='.5' fill='currentColor' style={dotStyle} variants={dotVariants[0]} animate={controls} />
        <motion.circle cx='17.5' cy='10.5' r='.5' fill='currentColor' style={dotStyle} variants={dotVariants[1]} animate={controls} />
        <motion.circle cx='8.5' cy='7.5' r='.5' fill='currentColor' style={dotStyle} variants={dotVariants[2]} animate={controls} />
        <motion.circle cx='6.5' cy='12.5' r='.5' fill='currentColor' style={dotStyle} variants={dotVariants[3]} animate={controls} />
      </svg>
    </div>
  );
});

PaletteIcon.displayName = 'PaletteIcon';

export { PaletteIcon };
